/**
 * Credit bundle configuration
 * Maps credit types to their Stripe price IDs and metadata
 *
 * Price IDs are loaded from environment files to support different
 * Stripe accounts per environment (dev/stage/prod).
 *
 * To add a new bundle:
 * 1. Add the lookupKey and bundle details here
 * 2. Run `cd manage && npm run start:<env>` > Stripe > Sync Credit Products
 * 3. Run Generate Client Config to get the price IDs
 * 4. Add the price IDs to each environment file
 */

import { environment } from "src/environments/environment";

export type CreditType = "image";

/** Lookup keys used to identify bundles in Stripe */
export type CreditLookupKey = "image_credits_50" | "image_credits_100";

export interface CreditBundle {
  /** Lookup key for this bundle (matches Stripe and environment config) */
  lookupKey: CreditLookupKey;
  /** Stripe Price ID (loaded from environment) */
  priceId: string;
  /** Number of credits in this bundle */
  credits: number;
  /** Price in cents (e.g., 1000 = $10.00) */
  priceInCents: number;
  /** Currency code */
  currency: string;
  /** Display name */
  name: string;
}

export interface CreditTypeConfig {
  /** Display label for this credit type */
  label: string;
  /** Description of what these credits are used for */
  description: string;
  /** Available bundles for purchase */
  bundles: CreditBundle[];
}

// Type for the environment credit prices
type CreditPrices = Record<CreditLookupKey, string>;

// Get price IDs from environment (with fallback for type safety)
const creditPrices: CreditPrices = (environment as { STRIPE_CREDIT_PRICES?: CreditPrices }).STRIPE_CREDIT_PRICES ?? {
  image_credits_50: "",
  image_credits_100: "",
};

/**
 * Credit configuration by type
 * Bundle metadata is defined here, price IDs come from environment
 */
export const CREDIT_CONFIG: Record<CreditType, CreditTypeConfig> = {
  image: {
    label: "Image",
    description: "For AI image generation",
    bundles: [
      {
        lookupKey: "image_credits_50",
        priceId: creditPrices.image_credits_50,
        credits: 50,
        priceInCents: 1250,
        currency: "usd",
        name: "50 Image Credits",
      },
      {
        lookupKey: "image_credits_100",
        priceId: creditPrices.image_credits_100,
        credits: 100,
        priceInCents: 2500,
        currency: "usd",
        name: "100 Image Credits",
      },
    ],
  },
};

/**
 * Get bundles for a specific credit type
 */
export function getCreditBundles(creditType: CreditType): CreditBundle[] {
  return CREDIT_CONFIG[creditType]?.bundles ?? [];
}

/**
 * Get config for a specific credit type
 */
export function getCreditTypeConfig(
  creditType: CreditType,
): CreditTypeConfig | undefined {
  return CREDIT_CONFIG[creditType];
}

/**
 * Format price for display
 */
export function formatCreditPrice(
  priceInCents: number,
  currency: string,
): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(priceInCents / 100);
}
